import React, { useMemo, useCallback, useEffect, useRef } from "react";import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  ColumnSeries,
  Tooltip,
  Inject,
  AreaSeries,
  Category,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

/* ---------- helpers ---------- */

function parseHourFromLabel(label) {
  const m = String(label || "").match(/(\d{2})\s*:\s*\d{2}/);
  if (!m) return null;
  const hh = Number(m[1]);
  return Number.isFinite(hh) ? hh : null;
}

function inferDaysCountFromLabels(labels) {
  const days = new Set();
  for (const l of labels || []) {
    const m = String(l || "").match(/^(\d{2}[./-]\d{2})\b/);
    if (m?.[1]) days.add(m[1]);
  }
  return days.size || 1;
}

/* ---------- component ---------- */

export const UniqueTvChart = ({
  series = [],
  chartType = "line",
  title = "Unique TVs by Day",
  loading = false,
  refreshKey,
  mode = "day",
}) => {
  const chartRef = useRef(null);
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);
  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";

  const { chartData, yAxisMax, labels } = useMemo(() => {
    const clean = (Array.isArray(series) ? series : []).map((p, i) => ({
      x: String(p?.label ?? ""),
      y: Number(p?.value) || 0,
      label: String(p?.label ?? ""),
      idx: i,
    }));

    const max = Math.max(...clean.map((d) => d.y), 0);

    return {
      chartData: clean,
      labels: clean.map((d) => d.label),
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
    };
  }, [series]);

  const rotateLabels = labels.length > 10;

  const hourStep = useMemo(() => {
    if (mode !== "hour") return 1;
    const daysCount = inferDaysCountFromLabels(labels);
    return daysCount <= 7 ? 6 : 8;
  }, [mode, labels]);

  const showMarkers = mode === "day";


  const handleAxisLabelRender = useCallback(
    (args) => {
      // Format Y-axis numbers with thousand separators
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) {
          args.text = formatNumber(value);
        }
        return;
      }

      if (args.axis?.name !== "primaryXAxis") return;

      const label = String(args.text ?? "");
      
      if (mode === "hour") {
        const hh = parseHourFromLabel(label);
        if (!Number.isFinite(hh) || hh % hourStep !== 0) {
          args.text = "";
          return;
        }
      }

      if (mode === "minute") {
        if (!label.endsWith(":00")) {
          args.text = "";
          return;
        }
      }

    },
    [mode, hourStep]
  );

  const handleTooltipRender = useCallback(
    (args) => {
      if (!args.point) return;
      const label = String(args.point.x ?? "");
      args.text = `${label} : ${formatNumber(args.point.y)}`;
    },
    []
  );

  return (
    <>
      <h3 className="chart-title">{title}</h3>
      {!loading && (
        <ChartComponent
          ref={chartRef}
          width="100%"
          height="420px"
          margin={chartType === "bar" ? { left: 30, right: 30 } : { left: 15, right: 40 }}
          primaryXAxis={{
            name: "primaryXAxis",
            valueType: "Category",
            rangePadding: chartType === "bar" ? "Additional" : "None",
            labelPlacement: "OnTicks",
            labelIntersectAction: rotateLabels ? "Rotate45" : "None",
            labelRotation: rotateLabels ? 45 : 0,
            majorGridLines: { width: 0 },
            majorTickLines: { width: 0 },
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          primaryYAxis={{
            name: "primaryYAxis",
            minimum: 0,
            maximum: yAxisMax,
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 },
            labelFormat: "n0",
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          tooltip={{ enable: true }}
          axisLabelRender={handleAxisLabelRender}
          tooltipRender={handleTooltipRender}
        >
          <Inject services={[LineSeries, ColumnSeries, AreaSeries, Category, Tooltip]} />

          <SeriesCollectionDirective>
            <SeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              type="Area"
              fill="#1976d2"
              opacity={0.12}
              border={{ width: 0 }}
            />

          <SeriesDirective
            dataSource={chartData}
            xName="x"
            yName="y"
            type={chartType === "bar" ? "Column" : "Line"}
            width={2}
            fill="#1976d2"
            border={{ color: "#1976d2", width: 2 }}
            marker={{
              visible: showMarkers,
              width: 7,
              height: 7,
              fill: "#fff",
              border: { width: 2, color: "#1976d2" },
            }}
            animation={{ enable: true, duration: 400 }}
          />
          </SeriesCollectionDirective>
        </ChartComponent>
      )}
    </>
  );
};

export default UniqueTvChart;
